'use client';
import React, { useEffect, useState } from 'react';
import { PencilSquareIcon as EditIcon } from '@heroicons/react/24/outline';
import Accordion from '@/components/molecules/accordion';
import InfoTooltip from '@/components/atoms/info-tooltip';
import useAccordions from '../settings/hooks/useAccordions';
import useCustomRouter from '@/hooks/useCustomRouter';
import useSwrClearCache from '@/hooks/useSwrClearCache';
import useAccount from '@/lib/hooks/useAccount';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import Modal from '@/components/organisms/modal';
import Typography from '@/components/atoms/typography';
import InputField from '@/components/atoms/floating-input';
import SelectField from '@/components/atoms/floating-select';
import MultiSelect from '@/components/atoms/multi-select';
import Link from 'next/link';
import Checkbox from '@/components/atoms/checkbox';
import Button from '@/components/atoms/button';
import { useParams } from 'next/navigation';
import Spinner from '@/components/organisms/spinner';
import { log } from 'node:console';
import { SettingsPageProps } from '../settings/types';
import DeleteAccountForm from '../settings/forms/delete-account';
import ResponsiveModal from '@/components/organisms/responsive-modal';
import { sdk } from '@/sdk/CommercetoolsSDK';
import toast from 'react-hot-toast';

const companyTypes = [
  { value: 'Commercial Maintenance', name: 'Commercial Maintenance' },
  { value: 'Domestic Maintenance & Renovation', name: 'Domestic Maintenance & Renovation' },
  { value: 'Commercial Projects', name: 'Commercial Projects' },
  { value: 'Multi Residential Projects', name: 'Multi Residential Projects' },
  { value: 'Vol. Home Builder Projects', name: 'Vol. Home Builder Projects' },
  { value: 'TAFE', name: 'TAFE' },
];

const size_of_business = [
  { value: '1', label: '1' },
  { value: '2-5', label: '2-5' },
  { value: '6-10', label: '6-10' },
  { value: '11-20', label: '11-20' },
  { value: '20+', label: '20+' },
];

const myroles = [
  { value: 'Apprentice Employee', label: 'Apprentice Employee' },
  { value: 'Employee', label: 'Employee' },
  { value: 'Owner', label: 'Owner' },
];

const companyTypeMigrationMap: Record<string, string> = {
  commercial: 'Commercial Projects',
  maintenance: 'Domestic Maintenance & Renovation',
  trade: 'TAFE',
  other: 'TAFE',
  residential: 'Multi Residential Projects',
};

const roles = [
  { value: 'Plumber', label: 'Plumber' },
  { value: 'Hydraulic Engineer', label: 'Hydraulic Engineer' },
  { value: 'Other', label: 'Other' },
];

const rolesMigrationMap: Record<string, string> = {
  'plumber': 'Plumber',
  'hydraulic': 'Hydraulic Engineer',
  'hydraulic engineer': 'Hydraulic Engineer',
  'other': 'Other',
};

const countries = [
  { value: 'au', label: 'Australia' },
  { value: 'nz', label: 'New Zealand' },
];

const auStates = [
  { value: 'act', label: 'ACT' },
  { value: 'nsw', label: 'NSW' },
  { value: 'nt', label: 'NT' },
  { value: 'qld', label: 'QLD' },
  { value: 'sa', label: 'SA' },
  { value: 'tas', label: 'TAS' },
  { value: 'vic', label: 'VIC' },
  { value: 'wa', label: 'WA' },
];

const nzStates = [
  { value: 'northisland', label: 'North Island' },
  { value: 'southisland', label: 'South Island' },
];

type FormData = {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone: string;
  companyType: string[];
  role: string;
  myrole: string;
  country: string;
  state: string;
  street:string;
  suburb:string;
  postalCode:string;
  termsAccepted: boolean;
  marketingAccepted: string; // 'true' | 'false'
  size_of_business: string;
};

const initialForm: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  company: '',
  phone: '',
  companyType: [],
  role: '',
  myrole: '',
  country: '',
  state: '',
  street: '',
  suburb: '',
  postalCode: '',
  termsAccepted: false,
  marketingAccepted: 'false',
  size_of_business: '',
};

const ProfileSettingsPage = ({ onDeleteAccount }: Partial<SettingsPageProps>) => {
  const extensions = sdk.composableCommerce;
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const isDeleteEnabled =
    confirmChecked && confirmText === 'DELETE';
  const { account, updateAccount } = useAccount();
  const router = useCustomRouter();
  const clearCache = useSwrClearCache();
  const [formData, setFormData] = useState<FormData>(initialForm);
  console.log("Form data in ProfileSettingsPage:", formData);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [successful, setSuccessful] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { locale } = useParams();
  const [loading, setLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setConfirmChecked(false);
    setConfirmText('');
  };
  const handleDeleteAccount = async () => {
    try {
      setLoading(true);
      const extensions = sdk.composableCommerce;
      const response: any = await extensions.account.deleteAccountWithoutPass();
      const statusCode = response?.StatusCode || response?.data?.StatusCode || response?.data?.data?.StatusCode;
      if (statusCode === 200) {
        await extensions.account.logout();
        toast.success('Account is successfully deleted', {
          position: 'bottom-right',
          duration: 5000,
        });
        router.replace('/login');
      } else {
        toast.error('Failed to delete Account. Try Again', {
          position: 'bottom-right',
          duration: 5000,
        });
        setError('Failed to delete Account. Try Again');
      }
    } catch (err) {
      toast.error('An error occurred. Please check console.');
    } finally {
      setLoading(false);
      setIsDeleteModalOpen(false);
    }
  };
  const [touched, setTouched] = useState<Record<keyof FormData, boolean>>(
    Object.keys(initialForm).reduce(
      (acc, key) => ({ ...acc, [key]: false }),
      {} as Record<keyof FormData, boolean>
    )
  );

  const emailValue = account?.email || '';

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, type, value, checked } = e.target as HTMLInputElement;
    if (name === 'email') return;

    if (name === 'phone') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length > 12) return;
      setFormData(prev => ({
        ...prev,
        phone: numericValue,
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? name === 'marketingAccepted'
            ? checked ? 'true' : 'false'
            : checked
          : value,
    }));
  };

  const handleBlur = (field: keyof FormData) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleCompanyTypeChange = (vals: string[]) => {
    setFormData(prev => ({ ...prev, companyType: vals }));
    setTouched(prev => ({ ...prev, companyType: true }));
  };

  const isError = (field: keyof FormData) => {
    if (field === 'companyType') {
      return touched.companyType && formData.companyType.length === 0;
    }
    return touched[field] && !formData[field];
  };

  const mandatoryFields: (keyof FormData)[] = [
    'firstName',
    'lastName',
    'email',
    'company',
    'phone',
    'companyType',
    'role',
    'myrole',
    'size_of_business',
    'country',
    'state',
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const hasError = mandatoryFields.some(field => {
      if (field === 'companyType') return formData.companyType.length === 0;
      return !formData[field];
    });
    if (hasError) {
      setTouched(prev =>
        Object.keys(prev).reduce(
          (acc, key) => ({ ...acc, [key]: true }),
          {} as Record<keyof FormData, boolean>
        )
      );
      setLoading(false);
      return;
    }

    const response = await updateAccount({
      ...formData,
      email: emailValue,
      companyType: formData.companyType.join(','),
      customer_marketing_consent: formData.marketingAccepted === 'true',
      onboarding_updated_datetime: account?.onboarding_updated_datetime,
      onboarding_workflow_status: 'MemberRegistered',
      ...(([
        account?.firstName,
        account?.lastName,
        account?.email,
        account?.companyName ?? account?.company,
        account?.phone,
        account?.company_type,
        account?.role,
        account?.myrole,
        account?.size_of_business,
        account?.country,
        account?.state,
      ].some((f) => Array.isArray(f) ? f.length === 0 : !f || String(f).trim().length === 0))
        ? { my_profile_updated: true }
        : {}),
    });

    if (response && response?.accountId) {
      setSuccessful('Profile has been successfully updated.');
      setTimeout(() => setSuccessful(''), 5000);
      setLoading(false);
    } else {
      setLoading(false);
      setIsModalOpen(true);
      setError('Changes could not be saved please try again later.');
    }
  };

  const stateOptions =
    formData.country === 'nz'
      ? nzStates
      : formData.country === 'au'
      ? auStates
      : [];

      
  const getUpdatedCompanyType = (value?: string): string => {
    if (!value) return '';
  
    const normalized = value.trim().toLowerCase();
    // If OLD value → replace
    if (companyTypeMigrationMap[normalized]) {
      return companyTypeMigrationMap[normalized];
    }
    // If already NEW value → return matching correct case from companyTypes
    const matchedNewValue = companyTypes.find(
      (type) => type.value.toLowerCase() === normalized
    );
    return matchedNewValue ? matchedNewValue.value : value;
  };

  const getUpdatedRole = (value?: string): string => {
    if (!value) return '';
  
    const normalized = value.trim().toLowerCase();
    // If OLD value → replace
    if (rolesMigrationMap[normalized]) {
      return rolesMigrationMap[normalized];
    }
    return value;
  };
  

  useEffect(() => {
    if (account) {
      // Debug logs for all relevant fields
      console.log('Account data:', {
        company_type: {
          raw: account?.company_type,
          normalized: account?.company_type?.toLowerCase(),
          match: companyTypes.find(opt => opt.value === account?.company_type?.toLowerCase())
        },
        role: {
          raw: account?.role,
          normalized: account?.role?.toLowerCase(),
          match: roles.find(opt => opt.value === account?.role?.toLowerCase())
        },
        country: {
          raw: account?.country,
          normalized: account?.country?.toLowerCase(),
          match: countries.find(opt => opt.value === account?.country?.toLowerCase())
        },
        state: {
          raw: account?.state,
          normalized: account?.state?.toLowerCase(),
          match: [...auStates, ...nzStates].find(opt => opt.value.toLowerCase() === account?.state?.toLowerCase())
        }
      });

      // country should not be changed when locale change
      const countryValue = account.country?.toLowerCase() ?? '';

      setFormData(prev => ({
        ...prev,
        firstName: account.firstName ?? '',
        lastName: account.lastName ?? '',
        email: account.email ?? '',
        company: account.companyName ?? '',
        phone: account.phone ?? '',
        companyType: account?.company_type
          ? account.company_type.split(',').map((s: string) => getUpdatedCompanyType(s.trim())).filter(Boolean)
          : [],
        role: getUpdatedRole(account.role),
        myrole: account.myrole ?? '',
        size_of_business: account.size_of_business ?? '',
        country: countryValue,
        state: account.state?.toLowerCase() ?? '',
        street: account.street?.toLowerCase() ?? '',
        suburb: account.suburb?.toLowerCase() ?? '',
        postalCode: account.postalCode?.toLowerCase() ?? '',              
        // Map marketing consent handling both string and boolean values with type safety
        marketingAccepted:
          account?.customer_marketing_consent === true || 
          (typeof account?.customer_marketing_consent === 'string' && account.customer_marketing_consent === 'true') 
            ? 'true' 
            : 'false',
      }));
    }
  }, [account, locale]);

  return (
    <>
      {loading && <Spinner />}
      {loading && (
        <div className="overlay fixed left-0 top-0 z-50 h-full w-full bg-gray-800 opacity-75"></div>
      )}

      <div className="flex flex-col py-8 md:px-4">
        <div className="w-full bg-white">
          <form
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
            onSubmit={handleSubmit}
            noValidate
          >
            {/* firstName */}
            <div>
              <InputField
                name="firstName"
                label="First Name*"
                value={formData.firstName}
                onChange={handleChange}
                onBlur={() => handleBlur('firstName')}
                error={isError('firstName')}
              />
              {isError('firstName') && (
                <span className="text-red-500 text-xs mt-1">Required</span>
              )}
            </div>

            {/* lastName */}
            <div>
              <InputField
                name="lastName"
                label="Last Name*"
                value={formData.lastName}
                onChange={handleChange}
                onBlur={() => handleBlur('lastName')}
                error={isError('lastName')}
              />
              {isError('lastName') && (
                <span className="text-red-500 text-xs mt-1">Required</span>
              )}
            </div>

            {/* email */}
            <div>
              <InputField
                name="email"
                label="Email Address*"
                type="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={() => handleBlur('email')}
                error={isError('email')}
                disabled={true}
              />
              {isError('email') && (
                <span className="text-red-500 text-xs mt-1">Required</span>
              )}
            </div>

            {/* phone */}
            <div>
              <InputField
                name="phone"
                label="Phone Number*"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                onBlur={() => handleBlur('phone')}
                error={isError('phone')}
              />
              {isError('phone') && (
                <span className="text-red-500 text-xs mt-1">Required</span>
              )}
            </div>

            {/* company */}
            <div>
              <InputField
                name="company"
                label="Company*"
                value={formData.company}
                onChange={handleChange}
                onBlur={() => handleBlur('company')}
                error={isError('company')}
              />
              {isError('company') && (
                <span className="text-red-500 text-xs mt-1">Required</span>
              )}
            </div>

            {/* companyType */}
            <div>
              <MultiSelect
                label="Company Type*"
                options={companyTypes}
                value={formData.companyType}
                onChange={handleCompanyTypeChange}
                className='text-base'
                required
              />
              {isError('companyType') && (
                <span className="text-red-500 text-xs mt-1">Required</span>
              )}
            </div>

            {/* role - My Trade */}
            <div>
              <SelectField
                name="role"
                label="My Trade*"
                options={roles}
                value={formData.role}
                onChange={handleChange}
                onBlur={() => handleBlur('role')}
                error={isError('role')}
                className='!max-h-60'
              />
              {isError('role') && (
                <span className="text-red-500 text-xs mt-1">Required</span>
              )}
            </div>

            {/* myrole - My Role */}
            <div>
              <SelectField
                name="myrole"
                label="My Role*"
                options={myroles}
                value={formData.myrole}
                onChange={handleChange}
                onBlur={() => handleBlur('myrole')}
                error={isError('myrole')}
                className='!max-h-60'
              />
              {isError('myrole') && (
                <span className="text-red-500 text-xs mt-1">Required</span>
              )}
            </div>

            {/* size_of_business - Size of Business */}
            <div>
              <SelectField
                name="size_of_business"
                label="Size of Business*"
                options={size_of_business}
                value={formData.size_of_business}
                onChange={handleChange}
                onBlur={() => handleBlur('size_of_business')}
                error={isError('size_of_business')}
                className='!max-h-60'
              />
              {isError('size_of_business') && (
                <span className="text-red-500 text-xs mt-1">Required</span>
              )}
            </div>

            <div>
              <InputField
                name="street"
                label="Address"
                value={formData.street}
                onChange={handleChange}
                onBlur={() => handleBlur('street')}
              />
            </div>

            <div>
              <InputField
                name="suburb"
                label="Suburb"
                value={formData.suburb}
                onChange={handleChange}
                onBlur={() => handleBlur('suburb')}
              />
            </div>

            <div>
              <InputField
                name="postalCode"
                label="Postal Code"
                value={formData.postalCode}
                onChange={handleChange}
                onBlur={() => handleBlur('postalCode')}
              />
            </div>

            {/* country */}
            <div>
              <SelectField
                name="country"
                label="Country*"
                options={countries}
                value={formData.country}
                onChange={handleChange}
                onBlur={() => handleBlur('country')}
                error={isError('country')}
                disabled={true}
              />
              {isError('country') && (
                <span className="text-red-500 text-xs mt-1">Required</span>
              )}
            </div>

            {/* state */}
            <div>
              <SelectField
                name="state"
                label="State*"
                options={stateOptions}
                value={formData.state}
                onChange={handleChange}
                className='!max-h-[10rem]'
                onBlur={() => handleBlur('state')}
                error={isError('state')}
              />
              {isError('state') && (
                <span className="text-red-500 text-xs mt-1">Required</span>
              )}
            </div>

            {/* <ProjectBuilderGuide/> */}

            {/* privacy notice + marketing consent */}
            <div className="md:col-span-2 space-y-3">
              <div className="text-14 font-normal text-gray-600">
                By submitting this form, you are providing your personal information to GWA to collect, use, and disclose in accordance with GWA's <Link href="/collection-statement" target="_blank" className="font-semibold text-16">Collection Statement</Link> and <Link href="/privacy/" target="_blank" className="text-16 font-semibold underline cursor-pointer">Privacy Policy</Link>.
              </div>
              <Checkbox
                name="marketingAccepted"
                className='border-2 '
                checked={formData.marketingAccepted === 'true'}
                onChange={handleChange}
                label={
                          <>
                              I subscribe to receive product updates, service information, and other relevant communications. I understand that I can update my preferences or unsubscribe at any time.
                          </>
                      }
              />
            </div>

            {/* Success/Error */}
            {(successful || error) &&(<div className="md:col-span-2">
              {successful ? (
                <span className="text-14 text-green-600">{successful}</span>
              ) : (
                error && (
                  <span className="text-xs font-normal leading-4 text-[#E32C53]">
                    {error}
                  </span>
                )
              )}
            </div>)}


              <Button
                variant="ghost"
                type="submit"
              className="mt-5 bg-black w-fit !px-4 cursor-pointer text-white text-16 font-medium py-[10px] rounded hover:bg-gray-800 transition"
              >
                Update Profile
              </Button>
              <br></br>
            <p
              onClick={() => setIsDeleteModalOpen(true)}
              className="text-16 text-gray-600 cursor-pointer hover:underline transition w-fit"
            >
              Delete Account?
            </p>
          </form>
        </div>
      </div>

      <Modal
        className="flex flex-col items-center justify-center !p-2"
        isOpen={isDeleteModalOpen}
        onRequestClose={handleCloseDeleteModal}
        closeButton
        variant="default"
        centered={true}
      >
        <div className="px-6 py-6">
          <Typography
            as="h4"
            className="pb-4 text-20 font-semibold text-gray-800"
          >
            Delete Account
          </Typography>

          <p className="text-sm text-gray-600 mb-4">
            Deleting your account will permanently remove your data, revoke access,
            and unsubscribe you from all communications. This action cannot be undone.
          </p>

          <div className="md:col-span-2 space-y-3 py-2">
            <Checkbox
              name="marketingAccepted"
              className='border-2 '
              checked={confirmChecked}
              onChange={(e) => setConfirmChecked(e.target.checked)}
              label={
                <>I understand that this action is permanent.</>
              }
            />
          </div>

          {/* Type DELETE */}
          <div className="mb-6">
            <label className="block text-sm text-gray-700 mb-1">
              Type <span className="font-semibold">DELETE</span> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={handleCloseDeleteModal}
            >
              Cancel
            </Button>

            <Button
              onClick={handleDeleteAccount}
              disabled={!isDeleteEnabled}
              className={`${isDeleteEnabled
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-red-300 text-white cursor-not-allowed'
                }`}
            >
              Delete Account
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ProfileSettingsPage;
